/**
 * Route Preloading Utility with TanStack Query Integration
 * Preload lazy-loaded routes AND their data on hover/touch to speed up navigation
 */

import type { QueryClient } from '@tanstack/react-query';
import { STALE_TIMES, GC_TIMES } from '../config/queryConfig';
import { hrKeys } from '../queries/hr/hrKeys';
import { receptionKeys } from '../queries/reception/receptionKeys';
import {
  fetchHrDashboardSafe,
  fetchHrEmployeesPageSafe,
} from '../queries/hr/hrQueryFns';
import {
  fetchReceptionBranchesQuery,
  fetchReceptionCoursesQuery,
} from '../queries/reception/receptionQueryFns';

// ============ Route Code Preloaders ============

// Map of route paths to their dynamic import functions
const ROUTE_PRELOADERS: Record<string, () => Promise<unknown>> = {
  // Public routes
  '/': () => import('../pages/public/Home'),
  '/about': () => import('../pages/public/About'),
  '/contact': () => import('../pages/public/Contact'),
  '/course': () => import('../pages/public/Courses'),
  '/programs': () => import('../pages/public/Programs'),
  '/placement-test': () => import('../pages/public/PlacementTest'),
  '/privacy': () => import('../pages/public/PrivacyPolicy'),
  '/termsofservice': () => import('../pages/public/TermsOfService'),
  '/job-applications': () => import('../pages/job-applications/PublicJobApplicationForm'),
  '/placement': () => import('../pages/public/placement/PlacementRegister'),
  '/placement/exam': () => import('../pages/public/placement/PlacementExam'),
  '/placement/status': () => import('../pages/public/placement/PlacementStatus'),
  '/placement/link-expired': () => import('../pages/errors/LinkExpired'),
  '/reception': () => import('../ReceptionDashboard'),
  '/reception/students': () => import('../ReceptionStudents'),
  '/reception/students/register': () => import('../pages/reception/StudentRegisterPage'),
  '/reception/waiting-insights': () => import('../pages/reception/WaitingPage'),
  '/reception/groups': () => import('../pages/reception/GroupsPage'),
  '/reception/quarter': () => import('../pages/reception/QuarterPage'),
  '/reception/materials': () => import('../pages/reception/language-admin-materials/LanguageAdminMaterialsPage'),
  '/reception/requests': () => import('../pages/reception/RequestsPage'),
  '/reception/course-interests': () => import('../pages/reception/CourseInterestsPage'),
  '/reception/sales-consult-slots': () => import('../pages/reception/SalesConsultationSlotsPage'),
  '/reception/sales-consult-queue': () => import('../pages/reception/SalesConsultQueuePage'),
  '/reception/calendar': () => import('../pages/reception/calendar-page'),
  '/reception/finance': () => import('../pages/reception/FinancePage'),
  '/reception/branch-finance': () => import('../pages/reception/BranchFinancePage'),
  '/reception/sales-invoices': () => import('../pages/reception/finance/SalesInvoicesPage'),
  '/reception/certificates': () => import('../pages/reception/CertificatesPage'),
  '/reception/placement-payments': () => import('../pages/reception/placement/PlacementPaymentsPage'),
  '/reception/refunds': () => import('../pages/reception/RefundsPage'),
  '/reception/tasks': () => import('../pages/reception/tasks/TaskList'),
  '/reception/tasks-manage': () => import('../pages/reception/tasks/TaskManagement'),
  '/reception/pro-finance': () => import('../pages/reception/ProFinancePage'),
  '/reception/daily-reports': () => import('../pages/reception/DailyReportsPage'),
  '/reception/reports/summary': () => import('../pages/reception/BranchSummaryPage'),
  '/reception/reports/group-balances': () => import('../pages/reception/GroupBalancesPage'),
  '/reception/reports/bookings': () => import('../pages/reception/BookingReportsPage'),
  '/reception/chat': () => import('../pages/reception/ChatPage'),
  '/reception/super/logs': () => import('../pages/reception/super-logs-page/SuperLogsPage'),
  '/reception/attendance/matrix': () => import('../pages/reception/AttendanceMatrixIndexPage'),
  '/reception/language': () => import('../pages/reception/language-admin-main-page'),
  '/reception/language/exams/import': () => import('../pages/reception/ImportExamPage'),
  '/reception/language/materials': () => import('../pages/reception/language-admin-materials/LanguageAdminMaterialsPage'),
  '/reception/language/students/inactive': () => import('../pages/reception/InactiveStudentsPage'),
  '/reception/exams': () => import('../pages/reception/ExamListPage'),
  '/set-password': () => import('../pages/auth/SetPassword'),
  // Sales routes
  '/sales': () => import('../pages/sales/SalesApp'),
  '/sales/today': () => import('../pages/sales/SalesApp'),
  '/sales/performance': () => import('../pages/sales/SalesApp'),
  '/sales/pipeline': () => import('../pages/sales/SalesApp'),
  '/sales/tasks': () => import('../pages/sales/SalesApp'),
  '/sales/history': () => import('../pages/sales/SalesApp'),
  '/sales/mail': () => import('../pages/sales/SalesApp'),
  '/sales/lead_movement': () => import('../pages/sales/SalesApp'),
  '/sales/tele-sales': () => import('../pages/sales/SalesApp'),
  '/sales/facebook_mappings': () => import('../pages/sales/SalesApp'),
  '/sales/targets': () => import('../pages/sales/SalesApp'),
  '/sales/reports': () => import('../pages/sales/SalesApp'),
  '/sales/settings': () => import('../pages/sales/SalesApp'),
  // HR routes
  '/hr': () => import('../pages/HRDashboardPage'),
  '/hr/dashboard': () => import('../pages/HRDashboardPage'),
  '/hr/employees': () => import('../pages/hr/employees-page/EnhancedHREmployeesPage'),
  '/hr/schedules': () => import('../pages/hr/HRSchedulesPage'),
  '/hr/mail': () => import('../pages/hr/HRMailPage'),
  '/hr/payroll': () => import('../pages/HRPayrollPage'),
  '/hr/instructor-payroll': () => import('../pages/hr/HRInstructorPayrollPage'),
  '/hr/group-rates': () => import('../pages/hr/HRGroupRatesPage'),
  '/hr/attendance': () => import('../pages/hr-attendance/HRAttendancePage'),
  '/hr/messages': () => import('../pages/hr/EnhancedHRMessagesPage'),
  '/hr/requests': () => import('../pages/hr/EnhancedHRRequestsPage'),
  '/hr/reports': () => import('../pages/hr/HRReportsPage'),
  // Student routes
  '/student/dashboard': () => import('../pages/student/dashboard/Dashboard'),
  '/student/profile': () => import('../pages/student/profile'),
  // Manager routes
  '/manager': () => import('../pages/manager/ManagerDashboard'),
  '/manager/employees': () => import('../pages/manager/EmployeesPage'),
  '/manager/mail': () => import('../pages/mail/MailCenterPage'),
  '/manager/finance': () => import('../pages/manager/ManagerFinancePage'),
  '/manager/tasks': () => import('../pages/manager/TasksPage'),
  '/manager/branch-transfers': () => import('../pages/manager/BranchTransfersPage'),
  '/manager/groups': () => import('../pages/reception/GroupsPage'),
  '/manager/quarter': () => import('../pages/reception/QuarterPage'),
  '/manager/students': () => import('../ReceptionStudents'),
  '/manager/calendar': () => import('../pages/reception/calendar-page'),
  '/manager/super/logs': () => import('../pages/reception/super-logs-page/SuperLogsPage'),
  '/manager/pro-finance': () => import('../pages/manager/ManagerProFinancePage'),
  '/manager/import-leads': () => import('../pages/sales/ImportLeadsPage'),
  '/accountant': () => import('../pages/accountant/AccountantShell'),
  '/accountant/dashboard': () => import('../pages/accountant/AccountantDashboardNew'),
  '/accountant/income': () => import('../pages/accountant/AccountantIncomePage'),
  '/accountant/expenses': () => import('../pages/accountant/AccountantExpensePage'),
  '/accountant/refunds': () => import('../pages/accountant/AccountantRefunds'),
  '/accountant/customer-estimates': () => import('../pages/accountant/AccountantCustomerEstimatesPage'),
  '/accountant/reports': () => import('../pages/accountant/AccountantReportsPage'),
  '/accountant/cash-flow': () => import('../pages/accountant/AccountantCashFlowPage'),
  '/accountant/installments': () => import('../pages/accountant/AccountantInstallmentsPage'),
  '/accountant/group-rates': () => import('../pages/accountant/AccountantGroupRatesPage'),
  '/marketing': () => import('../pages/marketing/MarketingShell'),
  '/marketing/courses': () => import('../pages/marketing/MarketingDashboardPage'),
  '/marketing/job-applications': () => import('../pages/marketing/JobApplicationsPage'),
  '/marketing/contact-inquiries': () => import('../pages/marketing/ContactInquiriesPage'),
  '/marketing/sales-invoices': () => import('../pages/marketing/MarketingSalesInvoicesPage'),
  '/employee/profile': () => import('../pages/employee/enhanced-profile-page'),
  '/employee/messages': () => import('../pages/employee/enhanced-messages-page'),
  '/employee/requests': () => import('../pages/employee/enhanced-employee-requests-page'),
  '/employee/attendance': () => import('../pages/employee/employee-attendance-page'),
  '/languages/student/exams': () => import('../pages/student/Exams'),
  '/instructor': () => import('../pages/instructor/InstructorDashboard'),
  '/instructor/profile': () => import('../pages/instructor/InstructorProfile'),
  '/developer/cockpit': () => import('../pages/developer/DeveloperCockpitPage'),
  '/developer/errors': () => import('../pages/developer/DeveloperCockpitPage'),
  '/developer/sandbox': () => import('../pages/developer/DeveloperCockpitPage'),
};

// ============ Route Data Prefetchers ============

// Map of route paths to their data prefetch functions
interface DataPrefetcher {
  queryKey: readonly unknown[];
  queryFn: () => Promise<unknown>;
  staleTime?: number;
  gcTime?: number;
}

const ROUTE_DATA_PREFETCHERS: Record<string, DataPrefetcher[]> = {
  '/reception': [
    {
      queryKey: receptionKeys.branches(),
      queryFn: fetchReceptionBranchesQuery,
      staleTime: STALE_TIMES.long,
      gcTime: GC_TIMES.long,
    },
    {
      queryKey: receptionKeys.courses(),
      queryFn: fetchReceptionCoursesQuery,
      staleTime: STALE_TIMES.long,
      gcTime: GC_TIMES.long,
    },
  ],
  '/hr': [
    {
      queryKey: hrKeys.dashboard(),
      queryFn: fetchHrDashboardSafe,
      staleTime: STALE_TIMES.medium,
      gcTime: GC_TIMES.extended,
    },
  ],
  '/hr/employees': [
    {
      queryKey: hrKeys.employees(),
      queryFn: fetchHrEmployeesPageSafe,
      staleTime: STALE_TIMES.short,
      gcTime: GC_TIMES.default,
    },
  ],
};

// Track which routes have been preloaded
const preloadedRoutes = new Set<string>();
const prefetchedData = new Set<string>();

// Reference to query client (set via setQueryClient)
let queryClientRef: QueryClient | null = null;

/**
 * Set the QueryClient reference for data prefetching
 * Call this once during app initialization
 */
export function setQueryClient(client: QueryClient): void {
  if (queryClientRef !== client) prefetchedData.clear();
  queryClientRef = client;
}

/**
 * Preload a route's code bundle
 * Safe to call multiple times - will only load once
 */
export function preloadRoute(path: string): void {
  // Normalize path (remove query strings and trailing slashes)
  const normalizedPath = path.split('?')[0].replace(/\/$/, '') || '/reception';
  
  // Check if already preloaded
  if (preloadedRoutes.has(normalizedPath)) return;
  
  // Find matching preloader
  const preloader = ROUTE_PRELOADERS[normalizedPath];
  if (!preloader) return;
  
  // Mark as preloaded immediately to prevent duplicate loads
  preloadedRoutes.add(normalizedPath);
  
  // Trigger preload (fire and forget)
  preloader().catch(() => {
    // If preload fails, allow retry
    preloadedRoutes.delete(normalizedPath);
  });
}

/**
 * Prefetch data for a route using TanStack Query
 * Safe to call multiple times - will only fetch once per stale window
 */
export function prefetchRouteData(path: string): void {
  if (!queryClientRef) return;

  const normalizedPath = path.split('?')[0].replace(/\/$/, '');
  
  // Check if already prefetched recently
  if (prefetchedData.has(normalizedPath)) return;
  
  const prefetchers = ROUTE_DATA_PREFETCHERS[normalizedPath];
  if (!prefetchers) return;
  
  prefetchedData.add(normalizedPath);
  
  // Clear from set after stale time to allow re-prefetch
  setTimeout(() => {
    prefetchedData.delete(normalizedPath);
  }, STALE_TIMES.medium);
  
  // Prefetch all queries for this route
  prefetchers.forEach((prefetcher) => {
    // Check if data is already fresh
    const existingData = queryClientRef!.getQueryData(prefetcher.queryKey);
    const state = queryClientRef!.getQueryState(prefetcher.queryKey);
    
    if (existingData && state && !state.isInvalidated) {
      const dataAge = Date.now() - (state.dataUpdatedAt || 0);
      if (dataAge < (prefetcher.staleTime ?? STALE_TIMES.medium)) {
        return; // Data is still fresh
      }
    }
    
    queryClientRef!.prefetchQuery({
      queryKey: prefetcher.queryKey,
      queryFn: prefetcher.queryFn,
      staleTime: prefetcher.staleTime ?? STALE_TIMES.medium,
      gcTime: prefetcher.gcTime ?? GC_TIMES.default,
    });
  });
}

/**
 * Preload route code AND prefetch route data
 * Combined preloading for optimal navigation performance
 */
export function preloadRouteWithData(path: string): void {
  preloadRoute(path);
  prefetchRouteData(path);
}

/**
 * Create an onMouseEnter handler for preloading
 */
export function createPreloadHandler(path: string): () => void {
  return () => preloadRoute(path);
}

/**
 * Create an onMouseEnter handler for preloading with data
 */
export function createPreloadWithDataHandler(path: string): () => void {
  return () => preloadRouteWithData(path);
}

/**
 * Preload multiple routes at once (for initial load optimization)
 */
export function preloadRoutes(paths: string[]): void {
  // Use requestIdleCallback if available, otherwise setTimeout
  const schedulePreload = typeof requestIdleCallback === 'function' 
    ? requestIdleCallback 
    : (fn: () => void) => setTimeout(fn, 100);
  
  schedulePreload(() => {
    paths.forEach(preloadRoute);
  });
}

/**
 * Preload multiple routes with data at once
 */
export function preloadRoutesWithData(paths: string[]): void {
  const schedulePreload = typeof requestIdleCallback === 'function' 
    ? requestIdleCallback 
    : (fn: () => void) => setTimeout(fn, 100);
  
  schedulePreload(() => {
    paths.forEach(preloadRouteWithData);
  });
}

/**
 * Get list of adjacent/related routes to preload
 * Based on common navigation patterns
 */
export function getRelatedRoutes(currentPath: string): string[] {
  const normalizedPath = currentPath.split('?')[0].replace(/\/$/, '');
  
  const relatedRoutes: Record<string, string[]> = {
    // Reception pages load on navigation/explicit link intent, not idle speculation.
    '/sales': ['/sales/performance', '/sales/pipeline', '/sales/tasks', '/sales/history'],
    '/sales/today': ['/sales/performance', '/sales/pipeline', '/sales/tasks'],
    '/sales/performance': ['/sales/pipeline', '/sales/history'],
    '/sales/pipeline': ['/sales/tasks', '/sales/history'],
    '/sales/tele-sales': [],
    '/hr': ['/hr/employees', '/hr/payroll', '/hr/messages'],
    '/hr/employees': ['/hr/payroll', '/hr/messages', '/hr/requests'],
    '/student/dashboard': ['/student/profile'],
    '/manager': ['/manager/employees', '/manager/tasks', '/manager/finance'],
  };
  
  return relatedRoutes[normalizedPath] || [];
}

/**
 * Preload related routes for the current page
 * Call this after the main page has loaded
 */
export function preloadRelatedRoutes(currentPath: string): void {
  const related = getRelatedRoutes(currentPath);
  if (related.length > 0) {
    preloadRoutes(related);
  }
}

/**
 * Reset route preload caches.
 * Used during logout to avoid stale role-adjacent preload decisions.
 */
export function resetRoutePreloadState(): void {
  preloadedRoutes.clear();
  prefetchedData.clear();
}

/**
 * Smart preloader that combines code splitting with data prefetching
 * Uses IntersectionObserver to preload when links become visible
 */
export function createVisibilityPreloader(): {
  observe: (element: HTMLElement, path: string) => void;
  disconnect: () => void;
} {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const path = (entry.target as HTMLElement).dataset.preloadPath;
          if (path) {
            preloadRouteWithData(path);
            observer.unobserve(entry.target);
          }
        }
      });
    },
    { rootMargin: '100px' }
  );

  return {
    observe: (element: HTMLElement, path: string) => {
      element.dataset.preloadPath = path;
      observer.observe(element);
    },
    disconnect: () => observer.disconnect(),
  };
}
